import { useNewCode } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'
import { CalendarIcon, InterfaceLinkBrokenIcon } from '@calendly/ui/components/icons'
import { useCallback, useContext } from 'react'

import { HoverCard } from 'components/hover_card'
import { useGQLGetMeetingQuery, transformLocationKind } from 'features/meetings'
import { useRightRail } from 'features/right_rail'
import { ShareProvider, ShareContext } from 'features/tracked_meetings/share/context'
import { ShareModal } from 'features/tracked_meetings/share/share_modal'
import { TemplateSummaryProvider } from 'features/tracked_meetings/template_summary_context'
import { useFetchTrackedMeetingWithSummariesByForeignIdQuery } from 'hooks/tracked_meetings/api'
import { CALENDLY_FOREIGN_EVENT_PROVIDER } from 'shared/utils'

import { FEATURE_FLAGS } from '../constants'
import { LinkBadge } from '../linkBadge'
import { MeetingHoverCard } from '../meetingHoverCard'
import { RecapSummarySection } from '../recapSummarySection'
import { getAttendeeNames, getFormattedDateTime } from '../utils'

/**
 * MeetingBadge component displays a meeting link in a badge format.
 *
 * This component displays a meeting title in a LinkBadge component with a calendar icon.
 * On hover, it shows a MeetingHoverCard with detailed meeting information.
 *
 * @param {Object} props - Component props
 * @param {string} [props.meetingId] - The ID of the meeting for navigation
 * @param {string} [props.title] - Fallback title if no meeting data is available
 * @param {boolean} [props.disabled] - Whether the badge is disabled
 * @param {Object} [props.analyticsHandlers] - Analytics handlers for tracking events
 *
 * @returns {JSX.Element} A badge component showing calendar icon and title
 *
 * @example
 * ```jsx
 * <MeetingBadge
 *   meetingId="abc123"
 *   title="Team Standup"
 *   analyticsHandlers={analyticsHandlers}
 * />
 *
 * <MeetingBadge
 *   title="Broken Meeting Link"
 *   disabled
 * />
 * ```
 */
const MeetingBadgeContent = ({ meetingId, title, disabled, analyticsHandlers }) => {
  const { open, close, isOpen } = useRightRail()
  const isHoverCardEnabled = useNewCode(FEATURE_FLAGS.MEETING_HOVER_CARD)?.enabled

  // Modal state managed at this level so modal persists when hover card closes
  const [isShareModalOpen, openShareModal, closeShareModal] = useBooleanState(false)
  const { postMeetingShare, share, shareUuid, setShare, setShareUuid } = useContext(ShareContext)

  // Fetch meeting information from GraphQL API
  const { data: gqlData, isLoading: isGqlLoading } = useGQLGetMeetingQuery(
    { uuid: meetingId },
    { skip: !meetingId || disabled }
  )

  // Fetch meeting intelligence data (summary) using the meeting UUID (foreignEventId)
  const { data: meetingIntelligence, isLoading: isMeetingIntelligenceLoading } = useFetchTrackedMeetingWithSummariesByForeignIdQuery(
    meetingId,
    { skip: !meetingId || disabled || !isHoverCardEnabled }
  )

  // Show loading state while requests are still in progress
  const isLoading = isGqlLoading || isMeetingIntelligenceLoading

  const onClick = () => {
    // skip click handler if hover card is enabled
    if (isHoverCardEnabled) {
      return
    }

    if (isOpen) {
      close()
    } else if (meetingId) {
      if (typeof analyticsHandlers?.trackNavigationLinkInChatClicked === 'function') {
        analyticsHandlers.trackNavigationLinkInChatClicked('meeting_link')
      }
      open('meeting_details', { eventId: meetingId })
    }
  }

  const icon = disabled ? <InterfaceLinkBrokenIcon /> : <CalendarIcon />

  const datetimeFormatted = getFormattedDateTime(gqlData?.start_time, gqlData?.end_time)

  // For physical locations, use the actual location value; otherwise transform the location kind
  let locationDisplay = ''
  if (gqlData?.location_kind) {
    locationDisplay = gqlData.location_kind === 'PHYSICAL'
      ? gqlData.location
      : transformLocationKind(gqlData.location_kind)
  }

  const attendeeNames = getAttendeeNames(gqlData?.attendees)
  const summaryText = meetingIntelligence?.summary
  const recapId = meetingIntelligence?.trackedMeetingUuid

  // Handler for share button click
  const handleShareRecapClick = useCallback(async () => {
    try {
      const response = await postMeetingShare({ event_uuid: recapId }).unwrap()
      setShare(response)
      const generatedShareUuid = response.uuid
      setShareUuid(generatedShareUuid)
      openShareModal()
    } catch (error) {
      console.error('Failed to create share link:', error)
    }
  }, [recapId, postMeetingShare, setShare, setShareUuid, openShareModal])

  const linkBadge = (
    <LinkBadge
      text={gqlData?.name || title}
      leftElement={icon}
      onClick={onClick}
      disabled={disabled}
      isLoading={isGqlLoading}
    />
  )

  const shareLink = `${window.location.origin}/s/meetings/${shareUuid}`

  // If disabled or no fetched data, or loading, or hover card is not enabled, just show the badge without hover card
  if (disabled || !gqlData || isLoading || !isHoverCardEnabled) {
    return linkBadge
  }

  return (
    <>
      <ShareModal
        isOpen={isShareModalOpen && !!shareUuid}
        isSnippet={false}
        onClose={closeShareModal}
        videoPageUrl={shareLink}
        onShareLinkClick={() => {}}
        share={share}
        shareUuid={shareUuid}
        attendeesFromSearchData={[]}
      />
      <HoverCard trigger={linkBadge}>
        <MeetingHoverCard
          title={gqlData.name || title}
          datetimeFormatted={datetimeFormatted}
          location={locationDisplay}
          attendees={attendeeNames}
          additionalContent={
            (summaryText && recapId) ? (
              <RecapSummarySection
                summary={summaryText}
                recapId={recapId}
                onShareClick={handleShareRecapClick}
              />
            ) : null
          }
          meetingId={meetingId}
        />
      </HoverCard>
    </>
  )
}

export const MeetingBadge = ({ meetingId, title, disabled, analyticsHandlers }) => {
  // For share functionality, we need the foreignEventId (meetingId) and foreignEventProvider
  // Since this is a Calendly meeting being fetched via GQL, we can default to 'Calendly'
  const foreignEventId = meetingId
  const foreignEventProvider = CALENDLY_FOREIGN_EVENT_PROVIDER

  // Fetch meeting intelligence data to get the recapId
  const isHoverCardEnabled = useNewCode(FEATURE_FLAGS.MEETING_HOVER_CARD)?.enabled
  const { data: meetingIntelligence } = useFetchTrackedMeetingWithSummariesByForeignIdQuery(
    meetingId,
    { skip: !meetingId || disabled || !isHoverCardEnabled }
  )
  const recapId = meetingIntelligence?.trackedMeetingUuid

  return (
    <ShareProvider
      foreignEventId={foreignEventId}
      foreignEventProvider={foreignEventProvider}
      eventUuid={recapId}
    >
      <TemplateSummaryProvider
        meetingUuid={recapId}
        isSample={false}
        isExternal={false}
      >
        <MeetingBadgeContent
          meetingId={meetingId}
          title={title}
          disabled={disabled}
          analyticsHandlers={analyticsHandlers}
        />
      </TemplateSummaryProvider>
    </ShareProvider>
  )
}
